import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { ApprovedUrl } from "@models/approvedUrl.model";
import { OrganizationService } from "@services/organizations/OrganizationService";

interface Props {
    /** Whose list to offer. The org plus its ancestors are shown, matching the old platform. */
    organizationId: number | null;
    value: string;
    /** `selected` is the chosen row, absent when the selection is cleared. */
    onChange: (url: string, selected?: ApprovedUrl) => void;
    label?: string;
    placeholder?: string;
    size?: "small" | "medium";
    disabled?: boolean;
}

/**
 * Page URL picker over the org's Approved URLs (MPN-1395 / MPN-1461).
 *
 * Only active URLs are offered, and the PSA list is deliberately absent — the old
 * platform never mixed it into an org's picker. A saved URL that is no longer on the
 * list still displays, with a warning, rather than silently blanking an existing ad.
 */
export default function ApprovedUrlPicker({
                                              organizationId,
                                              value,
                                              onChange,
                                              label = "Page URL",
                                              placeholder = "Search approved URLs...",
                                              size = "small",
                                              disabled = false,
                                          }: Props) {
    const [options, setOptions] = useState<ApprovedUrl[]>([]);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (organizationId == null || organizationId === -1) {
            setOptions([]);
            return;
        }
        const controller = new AbortController();
        setLoadFailed(false);
        new OrganizationService()
            .getApprovedUrls(organizationId, "inherited", true, controller.signal)
            .then(setOptions)
            .catch(() => {
                if (!controller.signal.aborted) setLoadFailed(true);
            });
        return () => controller.abort();
    }, [organizationId]);

    const selected = useMemo(
        () => options.find((o) => o.url === value) ?? null,
        [options, value],
    );

    // An ad built before the URL was retired, or before this picker existed.
    const offList = value !== "" && selected === null && options.length > 0;

    return (
        <>
            <Autocomplete
                size={size}
                disabled={disabled || loadFailed}
                options={options}
                value={selected}
                onChange={(_e, opt) => onChange(opt ? opt.url : "", opt ?? undefined)}
                getOptionLabel={(opt) => opt.title || opt.url}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                groupBy={(opt) =>
                    opt.organization_id === organizationId
                        ? "This organization"
                        : opt.organization_name ?? "Inherited"
                }
                filterOptions={(opts, state) => {
                    const input = state.inputValue.toLowerCase();
                    if (!input) return opts;
                    return opts.filter(
                        (o) =>
                            o.title.toLowerCase().includes(input) ||
                            o.url.toLowerCase().includes(input),
                    );
                }}
                renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt.id} sx={{ display: "block !important" }}>
                        <Typography sx={{ fontSize: "0.875rem" }}>{opt.title}</Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", wordBreak: "break-all" }}>
                            {opt.url}
                        </Typography>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField {...params} label={label} placeholder={placeholder} variant="outlined" />
                )}
            />
            {offList && (
                <Typography sx={{ fontSize: "0.75rem", color: "warning.main", mt: 0.5, wordBreak: "break-all" }}>
                    Not on the approved list: {value} — pick a replacement before publishing.
                </Typography>
            )}
            {loadFailed && (
                <Typography sx={{ fontSize: "0.75rem", color: "error.main", mt: 0.5 }}>
                    Could not load approved URLs.
                </Typography>
            )}
        </>
    );
}
